import { STORY_SOURCE, story } from '@/content/storyBuilder'

// Written on 2026-08-28 from the five-part full-page capture of
// digitopia.co/customers/penta-teknoloji/ — "birebir tıpa tıp aynısını
// tasarla" — with the page's own photography: Penta's headquarters in the
// header, the delivery van under "How Did it Start?" and the campus terrace
// beside "Key Outcomes". The last two fill halves the capture leaves blank,
// which is where Intel AI's and Yazaki's spare photographs went for the same
// reason. The closing quote is not a `section`: Yasemin Budak's words went
// into `content/testimonials/penta-teknoloji.ts`, which is where every other
// written page keeps its customer's words, and `StoryBody` draws that card.
export const added = story('penta-teknoloji', 'Penta Teknoloji', {
  doc: '6dada823-6b3d-43ed-9fe1-4b3544ab39f6-Penta Teknoloji and Digitopia.pdf',
  lede: [
    'Penta Teknoloji transformed its digital transformation journey from isolated technology '
    + 'initiatives into a company-wide alignment and acceleration programme.',
  ],
  heroPhoto: {
    src: '/photo/penta-teknoloji-headquarters.webp',
    alt: 'Penta Teknoloji’s headquarters: a three-storey glass and steel office block with '
      + 'the Penta wordmark along its top floor, under a clouded sky.',
  },
  sections: [
    {
      heading: 'Introduction',
      band: true,
      centred: true,
      body: [
        'Founded in 1990, Penta Teknoloji is one of Türkiye’s leading value-added technology '
        + 'distributors and an organisation that places digital thinking at the very core of '
        + 'its corporate culture. Established by computer engineers and shaped from day one by '
        + 'a strong technology-driven mindset, Penta Teknoloji has consistently positioned '
        + 'itself as a digital-first and innovation-oriented player within its ecosystem.',
        'Rather than viewing digitalisation as merely following trends, Penta Teknoloji '
        + 'approaches it as implementing the right technology at the right time. By focusing '
        + 'on the digitalisation of product, sales, and payment processes, the company '
        + 'launched early-stage initiatives that created differentiation within the sector. '
        + 'Over time, as digital capabilities expanded, aligning these capabilities across the '
        + 'organisation within a shared framework became increasingly critical.',
      ],
    },
    {
      heading: 'How Did it Start?',
      band: true,
      centred: true,
      body: [
        'Despite its strong digital heritage, Penta Teknoloji entered a phase where its '
        + 'digital and artificial intelligence investments required more conscious and '
        + 'strategic prioritisation. The key question was no longer which technology to use, '
        + 'but when and why to use it.',
        'To generate sustainable value from next-generation technologies, it became clear that '
        + 'data and analytics infrastructures were critical enablers for readiness in the '
        + 'AI-driven world. Determined not to turn digitalisation into a “technology stack,” '
        + 'Penta Teknoloji set out with the following objectives:',
      ],
      items: [
        { text: 'Understand its digital maturity level through an independent lens' },
        { text: 'Conduct a benchmark study not only with local peers but at a global scale' },
        {
          text: 'Test assumptions regarding both strengths and areas open for improvement',
        },
        { text: 'Assess the consistency of digital leadership across business units' },
        {
          text: 'Achieve alignment between Digital Transformation and Operations teams and '
            + 'business units',
        },
      ],
      after: [
        'Penta Teknoloji was already aware of the importance of measuring digital maturity and '
        + 'had participated in similar assessments in the past. However, both the organisation '
        + 'and the technology landscape had evolved significantly over time. As a result, '
        + 'Penta Teknoloji sought to reassess its current state using a more up-to-date, '
        + 'sector-specific, and structured model, and this is where its journey with Digitopia '
        + 'began. Penta Teknoloji chose Digitopia for its objective methodology, strong sector '
        + 'expertise, extensive benchmark database, industry-specific digital maturity model, '
        + 'and platform-based approach.',
      ],
      photo: {
        src: '/photo/penta-teknoloji-delivery-van.webp',
        alt: 'A red and white Penta delivery van parked outside the company’s offices.',
      },
    },
    {
      heading: 'Our Solution Approach',
      band: true,
      centred: true,
      body: [
        'Digitopia supported Penta Teknoloji through the Impact Platform and its Digital '
        + 'Maturity Index (DMI). DMI is a platform-supported digital maturity model that '
        + 'evaluates digital capabilities across six core dimensions: Customer, Operations, '
        + 'Innovation, People, Governance, and Technology.',
        'The engagement was designed around three core principles:',
      ],
      items: [
        {
          lead: 'Objective',
          text: 'Through a standardised and data-driven assessment approach, Penta Teknoloji’s '
            + 'current state was evaluated with a consistent and unbiased perspective, '
            + 'supported by a robust benchmark ecosystem.',
        },
        {
          lead: 'Collaborative',
          text: 'Cross-departmental workshops brought different business functions together, '
            + 'enabling the creation of a common language, making diverse perspectives '
            + 'visible, and establishing consensus around priorities.',
        },
        {
          lead: 'Actionable',
          text: 'Assessment outputs were translated into clear strategic priorities and an '
            + 'execution-ready roadmap, effectively bridging the gap between strategy and '
            + 'execution.',
        },
      ],
      after: [
        'Throughout the process conducted on the Impact Platform, Digitopia combined its '
        + 'human-centric expertise in facilitation, interpretation, and strategic framing with '
        + 'analytical and benchmark-driven insights. This ensured that outputs were repeatable '
        + 'and directly usable in decision-making processes.',
      ],
    },
    {
      heading: 'Key Outcomes',
      band: true,
      photo: {
        src: '/photo/penta-teknoloji-campus-terrace.webp',
        alt: 'Penta Teknoloji’s campus seen from its terrace: parasols and planted beds in '
          + 'the foreground, the office block with the Penta wordmark behind.',
      },
      photoSide: 'left',
      body: [
        'As a result of its collaboration with Digitopia, Penta Teknoloji achieved the '
        + 'following key outcomes:',
      ],
      items: [
        {
          lead: '360° Corporate Visibility:',
          text: 'A holistic view covering all business areas, clearly identifying both '
            + 'strengths and improvement areas.',
        },
        {
          lead: 'Strategic Alignment Across Teams:',
          text: 'Digital Transformation and Operations teams aligned with business units '
            + 'around a single digital vision, eliminating fragmented priorities.',
        },
        {
          lead: 'Clear Prioritisation and Focus:',
          text: 'Digital initiatives were evaluated based on value creation and readiness '
            + 'levels, allowing Penta Teknoloji to focus on areas that generate measurable '
            + 'business value.',
        },
        {
          lead: 'Rapid Transition from Strategy to Execution:',
          text: 'DMI outputs directly informed budgeting, project selection, and Objectives '
            + 'and Key Results (OKRs), extending impact from operational teams to top '
            + 'management.',
        },
        {
          lead: 'A Living Roadmap:',
          text: 'A living roadmap was created, defining short- and mid-term digital '
            + 'priorities, enriched with best practices, and actively guiding decision-making '
            + 'processes.',
        },
      ],
    },
    {
      heading: 'Conclusion',
      band: true,
      centred: true,
      body: [
        'Together with Digitopia, Penta Teknoloji transformed its digital transformation '
        + 'journey from isolated technology initiatives into a company-wide alignment and '
        + 'acceleration programme. Today, Penta Teknoloji stands out with a sustainable and '
        + 'scalable transformation model that advances technology, strategy, and people '
        + 'together.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a five-part full-page capture of '
    + `${STORY_SOURCE}/penta-teknoloji/ supplied on 2026-08-28, not from a file we hold, so `
    + 'please proofread it against the source before production. The closing card now carries '
    + 'both of the reference’s tabs — Ms Budak and Mr Özçelik, whose paragraph and portrait '
    + 'arrived on 2026-09-04 — so the only thing left needing a decision rather than a '
    + 'proofread is the usual template-wide differences from the reference: the header eyebrow '
    + 'reads “Digitopia / Customer Success Story” where digitopia.co reads “SUCCESS STORIES”; '
    + 'the headline is the company name, so the reference’s own “Turning Digital Maturity into '
    + 'a Strategic Advantage” survives only in the standfirst under it — say the word and it '
    + 'can become this page’s H1; and the reference repeats a “Read More” button inside four '
    + 'panels, which here is the one “Read the full story (PDF)” button in the header, '
    + 'pointing at the same document. One thing to look at: the capture leaves a tall blank '
    + 'gap after “How Did it Start?” and the whole left half of “Key Outcomes” empty, and two '
    + 'of the three photographs supplied have nowhere else to go, so they are placed there — '
    + 'confirm that is where they belong. Those two are 900px originals, which is what the '
    + 'live site serves; they are drawn at that width and will look soft on a high-density '
    + 'screen until larger files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 1
